// ABOUTME: React Query hook for fetching order change lead time configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/order-change-lead-times, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {OrderChangeScopeResponse, OrderChangeLeadTimeConfig, OrderChangeLeadTimeFormValues, toConfig, toFormValues} from '../types/orderChangeLeadTime'

export const useOrderChangeLeadTimeConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: OrderChangeLeadTimeConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<OrderChangeScopeResponse[], Error>({
    queryKey: ['orderChangeLeadTimeConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<OrderChangeScopeResponse[]>('/tuc-configs/order-change-lead-times', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootScopeData = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const parentValues: OrderChangeLeadTimeFormValues | undefined = rootScopeData
      ? {configurations: rootScopeData.configurations.map(toFormValues)}
      : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) return toConfig(match, parentValues)

    if (!rootScopeData) return toConfig({scopeId, configurations: []} as unknown as OrderChangeScopeResponse)

    const inherited = toConfig(rootScopeData)
    return {
      ...inherited,
      id: undefined,
      _meta: {...inherited._meta, isInherited: true},
      overrides: {configurations: false},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
